import { useState, useCallback, useEffect } from 'react';
import { TrendingUp } from 'lucide-react';
import { paretoVitalFewCount, runParetoAnalysisTool } from '../../tools/paretoAnalysis';
import { ItemInput } from './components/ItemInput';
import { ContributionView } from './components/ContributionView';
import { ResultsView } from './components/ResultsView';
import type { ParetoItem, ParetoEngineResults } from './types';
import ToolWrapper, { type ToolHostProps, type ToolResultItem } from '../../components/prioritization/ToolWrapper';
import { useToolSettings, type ToolItem } from '../../contexts/toolItemsHooks';
import { useLiveToolSession } from '../../components/prioritization/session/liveExperience/useLiveToolSession';
import { LiveToolScaffold } from '../../components/prioritization/session/liveExperience/LiveToolScaffold';
import { LiveSequentialScore } from '../../components/prioritization/session/liveExperience/liveTools/LiveSequentialScore';
import { LiveToolComplete } from '../../components/prioritization/session/liveExperience/liveTools/LiveToolComplete';

type AppState = 'input' | 'scoring' | 'results';

const PARETO_CONTRIBUTION_DEFINITION = 'This item\'s estimated share of total value across all items in scope. Contributions across all items should sum to 100%.';

function ParetoAnalysisContent(props: ToolHostProps) {
  const { isLiveSession, shouldAutoStart } = useLiveToolSession();

  const [state, setState] = useState<AppState>('input');
  const [items, setItems] = useState<ParetoItem[]>([]);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [recursiveLevel, setRecursiveLevel] = useState(0);
  // Live: the participant pressed Finish on the last item and sees their ranking.
  const [liveDone, setLiveDone] = useState(false);
  const [engineResults, setEngineResults] = useState<ParetoEngineResults | null>(null);

  const allScored = items.length > 0 && items.every(i => i.contribution != null);
  // The share of items in the top band — the session's setting, 20% by default.
  const { paretoThreshold } = useToolSettings();

  // Classification comes from the canonical engine adapter
  // (src/tools/paretoAnalysis.ts) — the page collects contributions; the
  // engine owns the top-band selection (the fewest items carrying 1 - threshold
  // of the value, per TOOL_SPEC).
  useEffect(() => {
    if (!allScored) {
      setEngineResults(null);
      return;
    }
    let cancelled = false;
    void runParetoAnalysisTool(
      items.map((item) => ({
        id: item.id,
        label: item.name,
        attributes: { contribution: item.contribution },
      })),
      null,
      { pareto_threshold: paretoThreshold }
    ).then((scoreSet) => {
      if (cancelled) return;
      setEngineResults(
        new Map(
          scoreSet.items.map((s) => [
            s.item_id as string,
            { rank: s.rank as number, isTopBand: s.is_top_20_percent as boolean },
          ])
        )
      );
    });
    return () => {
      cancelled = true;
    };
  }, [allScored, items, paretoThreshold]);
  
  // Track source IDs for imported items
  const [sourceIdMap, setSourceIdMap] = useState<Record<string, string>>({});

  // Handler for importing items from workspace
  const handleImportItems = useCallback((toolItems: ToolItem[]) => {
    const newItems: ParetoItem[] = toolItems.map((item) => ({
      id: item.id,
      name: item.name,
      contribution: null,
    }));
    
    // Track source IDs
    const newSourceIdMap: Record<string, string> = {};
    toolItems.forEach(item => {
      if (item.sourceId) {
        newSourceIdMap[item.id] = item.sourceId;
      }
    });
    setSourceIdMap(prev => ({ ...prev, ...newSourceIdMap }));
    setItems(prev => [...prev, ...newItems]);

    if (shouldAutoStart) {
      setCurrentItemIndex(0);
      setState('scoring');
    }
  }, [shouldAutoStart]);
  
  // Get results for saving as session result
  const handleGetResults = useCallback((): ToolResultItem[] => {
    // Sort by contribution (highest first)
    const sortedItems = [...items]
      .filter(item => item.contribution !== null)
      .sort((a, b) => (b.contribution || 0) - (a.contribution || 0));
    
    // Calculate cumulative percentage
    const totalContribution = sortedItems.reduce((sum, item) => sum + (item.contribution || 0), 0);
    let cumulativePercent = 0;
    
    const fallbackTopN = paretoVitalFewCount(sortedItems.map(item => item.contribution || 0), paretoThreshold);

    return sortedItems.map((item, idx) => {
      const percentage = totalContribution > 0 ? ((item.contribution || 0) / totalContribution) * 100 : 0;
      cumulativePercent += percentage;
      // Engine classification (the fewest items carrying 1 - threshold of the
      // value); the index fallback applies the same rule if the engine result
      // hasn't landed yet.
      const isTopBand = engineResults?.get(item.id)?.isTopBand ?? idx < fallbackTopN;

      return {
        id: sourceIdMap[item.id] || item.id,
        name: item.name,
        rank: engineResults?.get(item.id)?.rank ?? idx + 1,
        score: item.contribution || 0,
        category: isTopBand ? 'vital-few' : 'useful-many',
        classificationBreakdown: {
          paretoBand: isTopBand ? 'vital-few' : 'useful-many',
        },
        scoreBreakdown: {
          contribution: item.contribution || 0,
          percentage,
          cumulativePercent,
        },
        metadata: {
          contribution: item.contribution,
          percentage,
          cumulativePercent,
          isVitalFew: isTopBand,
        }
      };
    });
  }, [items, sourceIdMap, engineResults, paretoThreshold]);

  const handleStart = (itemNames: string[]) => {
    const newItems: ParetoItem[] = itemNames.map((name, index) => ({
      id: `item-${index}`,
      name,
      contribution: null,
    }));
    setItems(newItems);
    setCurrentItemIndex(0);
    setState('scoring');
  };
  
  const handleContributionUpdate = (value: number | null) => {
    const updatedItems = [...items];
    updatedItems[currentItemIndex] = {
      ...updatedItems[currentItemIndex],
      contribution: value,
    };
    setItems(updatedItems);
  };

  const handleNext = () => {
    if (currentItemIndex < items.length - 1) {
      setCurrentItemIndex(currentItemIndex + 1);
    } else if (isLiveSession) {
      setLiveDone(true);
    } else {
      setState('results');
    }
  };

  const handleBack = () => {
    if (currentItemIndex > 0) {
      setCurrentItemIndex(currentItemIndex - 1);
    }
  };

  const handleRecursivePareto = (topItems: ParetoItem[]) => {
    const newItems = topItems.map((item, index) => ({
      ...item,
      id: `item-${index}-recursive-${recursiveLevel + 1}`,
      contribution: null,
    }));
    setItems(newItems);
    setCurrentItemIndex(0);
    setRecursiveLevel(recursiveLevel + 1);
    setLiveDone(false);
    setState('scoring');
  };

  const handleReset = () => {
    setState('input');
    setItems([]);
    setCurrentItemIndex(0);
    setRecursiveLevel(0);
    setSourceIdMap({});
    setLiveDone(false);
  };

  const current = items[currentItemIndex];
  const canProceed = current?.contribution !== null && current?.contribution !== undefined;
  const canSubmitLive = isLiveSession && allScored;
  const contributionInput = current?.contribution?.toString() ?? '';

  return (
    <ToolWrapper
      toolName="Pareto Analysis"
      toolDescription="Identify the vital few that generate most value (80/20 rule)"
      onImportItems={handleImportItems}
      itemCount={items.length}
      onGetResults={canSubmitLive || state === 'results' ? handleGetResults : undefined}
      {...props}
    >
      <div className="w-full">
        {/* Header */}
        <div className="mb-6 pb-4 border-b border-[var(--rule)]">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <TrendingUp className="h-8 w-8 text-[var(--text-ter)] flex-shrink-0" />
              <div>
                <h1 className="text-2xl font-bold text-[var(--text-pri)]">
                  Pareto Analysis (80/20 Rule)
                </h1>
                <p className="text-sm text-[var(--text-sec)] mt-1">
                  Identify the top ~20% of items that generate ~80% of the value
                </p>
              </div>
            </div>
            {state !== 'input' && (
              <button
                onClick={handleReset}
                className="text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] px-3 py-1 rounded hover:bg-[var(--hover-tint)] whitespace-nowrap"
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div>
          {state === 'input' && (
            <>
              <ItemInput onStart={handleStart} />
            </>
          )}

          {state === 'scoring' && isLiveSession && liveDone && (
            <LiveToolScaffold
              title="Pareto analysis"
              subtitle="Your ranking is ready to submit"
              progressDone={items.length}
              progressTotal={items.length}
            >
              <LiveToolComplete
                heading="All items scored"
                items={handleGetResults().map((r, i) => ({
                  id: r.id,
                  name: r.name,
                  rank: r.rank ?? i + 1,
                  detail: `${(r.score ?? 0).toLocaleString()}% · ${r.category === 'vital-few' ? 'vital few' : 'useful many'}`,
                }))}
                onChangeScores={() => setLiveDone(false)}
              />
            </LiveToolScaffold>
          )}

          {state === 'scoring' && current && isLiveSession && !liveDone && (
            <LiveToolScaffold
              title="Pareto analysis"
              subtitle="Estimate each item's contribution to total value (%)"
              progressDone={items.filter(i => i.contribution != null).length}
              progressTotal={items.length}
            >
              <LiveSequentialScore
                itemName={current.name}
                itemIndex={currentItemIndex}
                totalItems={items.length}
                phaseTitle="Contribution"
                valueDefinition={PARETO_CONTRIBUTION_DEFINITION}
                phaseDescription="What % of total value does this item represent? (0–100)"
                placeholder="e.g. 25"
                inputValue={contributionInput}
                onInputChange={v => {
                  const num = v.trim() === '' ? null : parseFloat(v);
                  handleContributionUpdate(num != null && !isNaN(num) ? num : null);
                }}
                onNext={handleNext}
                onBack={currentItemIndex > 0 ? handleBack : undefined}
                canProceed={canProceed}
                isLastStep={currentItemIndex === items.length - 1}
              />
            </LiveToolScaffold>
          )}

          {state === 'scoring' && current && !isLiveSession && (
            <ContributionView
              item={current}
              itemIndex={currentItemIndex}
              totalItems={items.length}
              onContributionUpdate={handleContributionUpdate}
              onNext={handleNext}
              onBack={handleBack}
              canProceed={canProceed}
              progress={((currentItemIndex + 1) / items.length) * 100}
            />
          )}

          {state === 'results' && (
            <ResultsView
              items={items}
              engineResults={engineResults}
              onReset={handleReset}
              onRecursivePareto={handleRecursivePareto}
              recursiveLevel={recursiveLevel}
              threshold={paretoThreshold}
            />
          )}
        </div>
      </div>
    </ToolWrapper>
  );
}

export default function ParetoAnalysis(props: ToolHostProps) {
  return <ParetoAnalysisContent {...props} />;
}
